import { useMemo, useEffect, useState } from 'react';
import { useFilter } from '../context/FilterContext';

const API_URL = 'http://localhost:3001';

export const useFilteredData = () => {
  const { filters, matchesFilters } = useFilter();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API server
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_URL}/api/media`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const mediaData = await response.json();
        setData(mediaData);
      } catch (err) {
        console.error('Error fetching media data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper functions for calculations
  const calculateOverallScore = (channels) => {
    const scores = Object.values(channels).map(channel => channel.score);
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  };

  const calculateAverageRoas = (channels) => {
    const roas = Object.values(channels).map(channel => channel.roas);
    return (roas.reduce((sum, r) => sum + r, 0) / roas.length);
  };

  const calculateAverageCpa = (channels) => {
    const cpas = Object.values(channels).map(channel => channel.cpa);
    return Math.round(cpas.reduce((sum, cpa) => sum + cpa, 0) / cpas.length);
  };

  const calculateAverageCtr = (channels) => {
    const ctrs = Object.values(channels).map(channel => channel.ctr);
    return (ctrs.reduce((sum, ctr) => sum + ctr, 0) / ctrs.length);
  };

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter(media => 
      matchesFilters(
        media, 
        calculateOverallScore, 
        calculateAverageRoas, 
        calculateAverageCpa, 
        calculateAverageCtr
      )
    );
  }, [data, filters, matchesFilters]);

  // Calculate statistics for filtered data
  const statistics = useMemo(() => {
    const totalMedia = filteredData.length;
    const totalRevenue = filteredData.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.revenue, 0
      );
    }, 0);
    const totalSpent = filteredData.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.spent, 0
      );
    }, 0);
    const totalConversions = filteredData.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.conversions, 0
      );
    }, 0);
    const averageRoas = totalSpent > 0 ? (totalRevenue / totalSpent) : 0;
    const averageScore = filteredData.length > 0 ? 
      filteredData.reduce((sum, media) => sum + calculateOverallScore(media.channels), 0) / filteredData.length : 0;

    return {
      totalMedia,
      totalRevenue,
      totalSpent,
      totalConversions,
      averageRoas: parseFloat(averageRoas.toFixed(1)),
      averageScore: Math.round(averageScore)
    };
  }, [filteredData]);

  // Refetch function
  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/media`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const mediaData = await response.json();
      setData(mediaData);
    } catch (err) {
      console.error('Error refetching media data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    filteredData,
    statistics,
    loading,
    error,
    refetch,
    hasActiveFilters: filters.performance !== 'all' || 
                     filters.channels.length > 0 || 
                     filters.searchTerm || 
                     filters.dateRange.preset !== 'last30days'
  };
};
